<template>
  <div>
    <div
      v-if="editMode"
      class="d-flex align-center"
    >
      <EditCell
        v-model="internalValue"
        :field="field"
      />
      <v-btn
        text
        class="theme-btn validate-btn ml-md-2 ml-0"
        @click="validateChange"
      >{{ $t('common.action.validate') }}
      </v-btn>
      <v-btn
        text
        class="theme-btn error-btn ml-md-2 ml-0"
        @click="cancelChange"
      >{{ $t('common.action.cancel') }}
      </v-btn>
    </div>
    <div
      v-else
      class="d-flex align-center color-label"
    >
      <div v-if="formatDisplay[field] && internalValue">
        {{ $t(`main.enums.${field}.${internalValue}`) }}
      </div>
      <div
        v-else-if="field === 'birth_insee_code'"
        :class="{
          'not-specified-data': !internalValue,
        }"
      >
        {{ formatInsee(internalValue) }}
      </div>
      <div
        v-else-if="field === 'date_of_birth'"
        :class="{
          'not-specified-data': !internalValue,
        }"
      >
        {{ formatBirthDate(internalValue) }}
      </div>
      <div v-else-if="internalValue">
        {{ internalValue }}
      </div>
      <div
        v-else
        class="not-specified-data"
      >
        {{ $t('common.not_specified') }}
      </div>
      <v-btn
        v-if="displayEditBtn"
        v-ripple="false"
        class="btn-wesave no-padding"
        text
        :disabled="disabled"
        @click="toggleEditMode"
      >
        <v-img
          width="20px"
          height="20px"
          contain
          :class="{ 'disabled-cell': disabled }"
          :src="'./assets/icons/edit-table.svg'"
        />
      </v-btn>
    </div>
  </div>
</template>

<script>
  import EditCell from '@/components/ui/table/EditCell.vue'
  import CommonMixin from '@/mixins/store/CommonMixin'
  import AdminMixin from '@/mixins/store/AdminMixin'
  import ApiService from '@/services/ApiService'
  import { AUTHORIZATIONS } from '@/services/models.const'

  export default {
    components: {
      EditCell,
    },
    mixins: [CommonMixin, AdminMixin,],
    props: {
      value: {
        type: [String, Number, Boolean, Array, null,],
        default: null,
      },
      field: {
        type: String,
        required: true,
      },
      type: {
        type: String,
        required: true,
      },
      disabled: {
        type: Boolean,
        required: true,
      },
    },
    data() {
      return {
        editMode: false,
        formatDisplay: {
          marital_status: true,
          matrimonial_regime: true,
          pacs_convention: true,
        },
      }
    },
    computed: {
      internalValue: {
        get() {
          if (Array.isArray(this.value)) {
            return this.value.join(', ')
          }

          return this.value
        },
        set(value) {
          this.$emit('input', value)
        },
      },
      displayEditBtn() {
        if (this.authorization === AUTHORIZATIONS.readOnly) {
          return false
        }

        return ['personal_infos', 'contact_infos',].includes(this.type)
      },
    },
    methods: {
      toggleEditMode() {
        this.editMode = !this.editMode
        this.$emit('editMode', this.editMode)
      },
      async validateChange() {
        const payload = {
          type: 'people',
          attributes: {},
        }
        if (this.field === 'nationalities') {
          payload.attributes[this.field] = this.internalValue.split(',')
        } else {
          payload.attributes[this.field] = this.internalValue
        }
        this.toggleAppLoader(true)
        const result = await ApiService.updatePerson(this.currentCustomer.id, payload)
        this.toggleAppLoader(false)
        if (result.errors) {
          this.$message({
            type: 'error',
            text: 'update_infos_error',
          })
          this.$store.commit('admin/setFormErrors', result.errors)
        } else {
          this.$message({
            type: 'success',
            text: 'update_infos_success',
          })
          this.refreshCustomerFields()
          this.toggleEditMode()
        }
      },
      cancelChange() {
        this.$emit('resetField', this.field)
        this.$store.commit('admin/resetFormErrors')
        this.toggleEditMode()
      },
      formatInsee(value) {
        if (!value) {
          return this.$t('common.not_specified')
        }
        const cities = this.currentConfig.enums.french_cities_with_state
        const city = cities[value]

        return city ? `${String(city.dep).padStart(2, '0')} - ${city.libelle}` : null
      },
      formatBirthDate(value) {
        if (!value) {
          return this.$t('common.not_specified')
        }
        const ageDifMs = Date.now() - new Date(value).getTime()
        const ageDate = new Date(ageDifMs)
        const age = Math.abs(ageDate.getUTCFullYear() - 1970)

        return `${this.$d(new Date(value))}
        (${this.$tc('main.form.age', age, { nb: age, })})`
      },
    },
  }
</script>

<style lang="scss">
.disabled-cell {
  opacity: 0.3;
  pointer-events: none;
}
.not-specified-data {
  color: $color-49495c;
  opacity: 0.5;
}
.color-label {
  color: $color-4d4d4f;
}
.validate-btn {
  background-color: $color-00d367;
  color: $color-ffffff !important;
}
.error-btn {
  background-color: $color-ff524f;
  color: $color-ffffff !important;
}
</style>
